import { SpaLandingPageVersion } from '@features/spa/pages/SpaLandingPage';
import { Image } from 'shared-types/Common';
import { Property } from 'shared-types/Property';
import * as entityActions from '@store/entity/actions';
import { NormalizedStore } from '@store/entity/entity.types';
import { entitySelectors } from '@store/entity/selectors';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import React from 'react';
import { useTranslations } from '@lang';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { fetchNavigationLabels, labelKeys } from '@features/Menu';
import {
  getSpaTreatmentsPagePrefetch,
  getAvailableSpaTreatmentsPrefetch,
  getSpaDaysPagePrefetch,
  getSpaStaysPagePrefetch,
  getSpaSettingsPrefetch,
  getSpaFacilitiesPagePrefetch,
  getAvailableSpaPackagesPrefetch,
  getAllSpaPackagesPrefetch,
} from '@features/spa/hooks';
import { getAvailableSpaFacilitiesPrefetch } from '@features/spa/hooks';
import { yourJourneySelectors } from '@store/yourJourney';
import { parseDateToString } from '@utils/helpers';

type SpaPageType = {
  heroBackgroundImage: Image;
  content: string;
  title: string;
};

type SpaProps = {
  language: string;
  socialFallbackImage: Image;
  url: string;
  spaPage: SpaPageType;
  property: Property;
};

const Spa: NextPage<SpaProps> = ({ language, url, spaPage, socialFallbackImage }) => {
  const { t } = useTranslations();
  const pageName = t('app.spa');

  const pageTitle = spaPage?.title ? `${pageName} | ${spaPage.title}` : pageName;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          openGraph: {
            title: pageTitle,
            locale: language,
            url,
            images: [{ url: socialFallbackImage && socialFallbackImage.url }],
          },
        }}
      />
      <SpaLandingPageVersion language={language} />
    </>
  );
};

type SpaInitialProps = {
  property: Property;
  store: NormalizedStore;
} & NextPageContext &
  Pick<SpaProps, 'language' | 'url'>;

Spa.getInitialProps = async ({
  store,
  property,
  language,
  url,
  res,
}: SpaInitialProps): Promise<SpaProps> => {
  const { slug: siteSlug } = property;
  const { spaDate } = yourJourneySelectors.getParsedJourneyParams(store.getState());

  const client = new QueryClient();
  await client.prefetchQuery({
    queryKey: labelKeys.labels(siteSlug),
    queryFn: () => fetchNavigationLabels({ siteSlug }),
  });
  const isServer = !!res;

  if (property) {
    await Promise.all([
      ...(isServer
        ? [
            getAvailableSpaTreatmentsPrefetch(client, property.id, language, null, null, 4),
            getAvailableSpaFacilitiesPrefetch(client, property.id, language, null, null, 4),
            getAvailableSpaTreatmentsPrefetch(
              client,
              property.id,
              language,
              parseDateToString(spaDate),
              null,
              4
            ),
            getAvailableSpaFacilitiesPrefetch(
              client,
              property.id,
              language,
              parseDateToString(spaDate),
              null,
              4
            ),
            getSpaSettingsPrefetch(client, property.id),
            getSpaTreatmentsPagePrefetch(client, property.id, language),
            getSpaFacilitiesPagePrefetch(client, property.id, language),
            getSpaDaysPagePrefetch(client, property.id, language),
            getSpaStaysPagePrefetch(client, property.id, language),
            getAvailableSpaPackagesPrefetch(
              client,
              property.id,
              { category: null, date: parseDateToString(spaDate) },
              language
            ),
            getAllSpaPackagesPrefetch(client, property.id, language),
          ]
        : []),
      entityActions.fetchHomepage(property.id, language)(store.dispatch),
      entityActions.fetchSpaPage(property.id, language)(store.dispatch),
    ]);
  }

  const spaPage = entitySelectors.getSpaPage(store.getState());

  return {
    spaPage,
    url,
    language,
    property,
    socialFallbackImage: property.socialFallbackImage,
    dehydratedState: dehydrate(client),
  } as SpaProps;
};

export default Spa;
